import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";

/**
 * doc-editor controller
 *
 * Lives on the <body> element so it is always in scope, regardless of where the
 * "open" action is triggered (file row, search result, etc.).
 *
 * Targets:
 *   modal          — Bootstrap modal root div (#docEditorModal)
 *   modalBody      — .modal-body inside the modal (container is appended here)
 *   title          — modal title element showing the filename
 *   container      — div where DocsAPI.DocEditor renders its iframe
 *   loader         — loading overlay shown during initialisation
 *   newFileOverlay — file-type selection overlay for onRequestCreateNew
 *   nameModal      — Bootstrap modal asking for the new file name
 *   nameInput      — text input inside nameModal
 *   nameExt        — extension suffix shown next to nameInput
 *   driveFrame     — <turbo-frame id="drive-content-area"> on Drive pages;
 *                    refreshed after a new file is created
 *
 * Values:
 *   server-url  — base URL of the Document Server
 *   create-url  — POST endpoint for creating a blank document
 *
 * Actions (callable from any element on the page):
 *   click->doc-editor#open
 *     data-doc-editor-blob-id-param
 *     data-doc-editor-config-url-param
 *     data-doc-editor-filename-param
 *
 *   click->doc-editor#openNew
 *     data-doc-editor-file-type-param   ("docx" | "xlsx" | "pptx")
 */
export default class extends Controller {
  static targets = ["modal", "modalBody", "title", "container", "loader",
                    "newFileOverlay", "nameModal", "nameInput", "nameExt",
                    "driveFrame"];
  static values  = { serverUrl: String, createUrl: String };

  // ID passed to DocsAPI.DocEditor — must match the container element's id
  // which is set in the HTML and restored in _restoreContainer().
  static CONTAINER_ID = "doc-editor-container";

  static DEFAULT_BASE_NAMES = {
    docx: "Dokumentum",
    xlsx: "Táblázat",
    pptx: "Bemutató",
  };

  connect() {
    this._bsModal         = null;
    this._nameBsModal     = null;
    this._editor          = null;
    this._newFileResolve  = null;
    this._nameResolve     = null;
    this._pendingFileType = null;
    this._namePromptActive = false;
  }

  disconnect() {
    this._destroyEditor();
  }

  // ── Public actions ────────────────────────────────────────────────────────

  // Open an existing Drive file in the editor.
  async open(event) {
    event.preventDefault();
    const { blobId, configUrl, filename } = event.params;
    if (!blobId || !configUrl) return;
    await this._openWithConfig(configUrl, filename || "");
  }

  // Ask for a filename, create a blank Office file, then open the editor.
  async openNew(event) {
    event?.preventDefault?.();
    const fileType = event.params.fileType;
    if (!fileType || !this.hasCreateUrlValue) return;

    const filename = await this._askFileName(fileType);
    if (!filename) return;

    this._showModal(filename);

    try {
      await this._ensureApiLoaded();

      const { config_url, filename: createdName } = await this._createFile(fileType, filename);
      if (this.hasTitleTarget) this.titleTarget.textContent = createdName;

      this._refreshDriveFrame();
      await this._initEditor(config_url);
    } catch (err) {
      console.error("[doc-editor] Failed to create new file:", err);
      this._showEditorError(err.message);
    }
  }

  // Close the editor modal.
  close() {
    this._cancelNewFile();
    this._cancelFileNamePrompt();
    this._destroyEditor();
    this._bsModal?.hide();
  }

  // Confirm file-type selection in the new-file overlay.
  newFileConfirm(event) {
    if (this._newFileResolve) {
      this._newFileResolve(event.params.type);
      this._newFileResolve = null;
    }
    this._hideNewFileOverlay();
  }

  // Cancel the new-file overlay.
  newFileCancel() {
    this._cancelNewFile();
  }

  // Confirm the name prompt modal and resolve with "basename.ext".
  confirmFileName(event) {
    event.preventDefault();
    if (!this.hasNameInputTarget || !this._pendingFileType) return;

    const ext = this._pendingFileType;
    let base = this.nameInputTarget.value.trim();
    if (!base) {
      this.nameInputTarget.focus();
      return;
    }

    const extSuffix = `.${ext}`;
    if (base.toLowerCase().endsWith(extSuffix)) {
      base = base.slice(0, -extSuffix.length).trim();
    }
    if (!base) {
      this.nameInputTarget.focus();
      return;
    }

    const filename = `${base}${extSuffix}`;
    const resolve = this._nameResolve;
    this._namePromptActive = false;
    this._nameResolve = null;
    this._pendingFileType = null;
    this._nameBsModal?.hide();
    resolve?.(filename);
  }

  // Cancel the name prompt without creating a file.
  cancelFileName(event) {
    event?.preventDefault();
    this._cancelFileNamePrompt();
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  async _openWithConfig(configUrl, filename) {
    this._showModal(filename);
    try {
      await this._ensureApiLoaded();
      await this._initEditor(configUrl);
    } catch (err) {
      console.error("[doc-editor] Failed to open editor:", err);
      this._showEditorError(err.message);
    }
  }

  async _initEditor(configUrl) {
    const config = await this._fetchConfig(configUrl);

    config.editorConfig       = config.editorConfig || {};
    config.editorConfig.lang  = "hu";

    config.events              = config.events || {};
    config.events.onDocumentReady = () => this._hideLoader();
    config.events.onAppReady      = () => this._hideLoader();

    if (this.hasCreateUrlValue) {
      config.events.onRequestCreateNew = () => this._handleCreateNew();
    }

    this._restoreContainer();

    this._editor = new window.DocsAPI.DocEditor(
      this.containerTarget.id,
      config
    );
  }

  // POST to the create endpoint and return { config_url, filename }.
  // Passes current Drive folder so the blank file appears in the open folder.
  async _createFile(fileType, filename = null) {
    const body = { file_type: fileType }
    if (filename) body.filename = filename
    const folderId = this._currentDriveFolderId()
    if (folderId) body.folder_id = folderId

    const response = await fetch(this.createUrlValue, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept":       "application/json",
        "X-CSRF-Token": this._csrfToken,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Create failed: ${response.status}`);
    return response.json();
  }

  // Bootstrap modal asking for the new file basename before create.
  _askFileName(fileType) {
    return new Promise((resolve) => {
      const modalEl = this._nameModalElement();
      if (!modalEl) {
        console.error("[doc-editor] Name modal (#newOfficeFileModal) missing from DOM");
        resolve(null);
        return;
      }

      this._nameResolve = resolve;
      this._pendingFileType = fileType;
      this._namePromptActive = true;

      if (this.hasNameExtTarget) this.nameExtTarget.textContent = `.${fileType}`;
      const input = this.hasNameInputTarget
        ? this.nameInputTarget
        : modalEl.querySelector("[data-doc-editor-target='nameInput']");
      if (input) {
        input.value = this.constructor.DEFAULT_BASE_NAMES[fileType] || "Fájl";
      }

      if (!this._nameBsModal) {
        this._nameBsModal = Modal.getOrCreateInstance(modalEl);
        modalEl.addEventListener(
          "hidden.bs.modal",
          () => {
            if (!this._namePromptActive) return;
            this._namePromptActive = false;
            if (this._nameResolve) {
              this._nameResolve(null);
              this._nameResolve = null;
              this._pendingFileType = null;
            }
          },
          { passive: true }
        );
        modalEl.addEventListener(
          "shown.bs.modal",
          () => {
            const el = this.hasNameInputTarget
              ? this.nameInputTarget
              : modalEl.querySelector("[data-doc-editor-target='nameInput']");
            if (!el) return;
            el.focus();
            el.select();
          },
          { passive: true }
        );
      }

      this._nameBsModal.show();
    });
  }

  _nameModalElement() {
    if (this.hasNameModalTarget) return this.nameModalTarget;
    return document.getElementById("newOfficeFileModal");
  }

  _cancelFileNamePrompt() {
    this._namePromptActive = false;
    if (this._nameResolve) {
      this._nameResolve(null);
      this._nameResolve = null;
    }
    this._pendingFileType = null;
    this._nameBsModal?.hide();
  }

  _currentDriveFolderId() {
    const fromStore = window._driveStore?.currentFolder?.value?.id
    if (fromStore != null && fromStore !== "" && Number.isFinite(Number(fromStore))) {
      return String(fromStore)
    }
    const el = document.querySelector("[data-drive-current-folder-value]")
    const raw = el?.dataset?.driveCurrentFolderValue
    if (raw && /^\d+$/.test(raw)) return raw
    return null
  }

  _showEditorError(message) {
    this._hideLoader();
    if (this.hasContainerTarget) {
      this.containerTarget.innerHTML =
        `<div class="alert alert-danger m-3">Hiba: ${message}</div>`;
    }
  }

  // Reload the Drive content so newly created files appear in the list
  // while the editor modal is open.
  // After the Preact refactor the drive content is a plain <div>, not a
  // turbo-frame, so we call window._driveStore.loadFolder() when available.
  _refreshDriveFrame() {
    if (window._driveStore) {
      const path = window.location.pathname + '.json' + window.location.search;
      window._driveStore.loadFolder(path);
      return;
    }
    // Fallback for legacy Turbo Frame views
    if (!this.hasDriveFrameTarget) return;
    this.driveFrameTarget.src = window.location.href;
  }

  _showModal(filename) {
    if (this.hasTitleTarget) this.titleTarget.textContent = filename;
    this._showLoader();

    if (!this._bsModal && this.hasModalTarget) {
      this._bsModal = new Modal(this.modalTarget, { backdrop: "static", keyboard: false });
      this.modalTarget.addEventListener(
        "hidden.bs.modal",
        () => { this._cancelNewFile(); this._destroyEditor(); },
        { passive: true }
      );
    }
    this._bsModal?.show();
  }

  _showLoader() {
    if (this.hasLoaderTarget) this.loaderTarget.classList.remove("d-none");
  }

  _hideLoader() {
    if (this.hasLoaderTarget) this.loaderTarget.classList.add("d-none");
  }

  _destroyEditor() {
    if (this._editor) {
      try { this._editor.destroyEditor(); } catch (_) { /* already gone */ }
      this._editor = null;
    }
    this._restoreContainer();
  }

  // Ensures the container div exists and is empty before each editor init.
  // OnlyOffice removes the placeholder on destroyEditor(), so we recreate it.
  _restoreContainer() {
    if (this.hasContainerTarget) {
      this.containerTarget.innerHTML = "";
      return;
    }
    if (!this.hasModalBodyTarget) return;

    const el = document.createElement("div");
    el.id    = this.constructor.CONTAINER_ID;
    el.dataset.docEditorTarget = "container";
    el.style.cssText = "height: 100%; width: 100%;";
    this.modalBodyTarget.appendChild(el);
  }

  // Handles the onRequestCreateNew event fired by OnlyOffice.
  async _handleCreateNew() {
    const fileType = await this._askNewFileType();
    if (!fileType) return;

    const filename = await this._askFileName(fileType);
    if (!filename) return;

    try {
      const { config_url, filename: createdName } = await this._createFile(fileType, filename);

      this._destroyEditor();
      if (this.hasTitleTarget) this.titleTarget.textContent = createdName;
      this._showLoader();
      this._refreshDriveFrame();
      await this._initEditor(config_url);
    } catch (err) {
      console.error("[doc-editor] Failed to create new file:", err);
    }
  }

  _askNewFileType() {
    return new Promise((resolve) => {
      this._newFileResolve = resolve;
      this._showNewFileOverlay();
    });
  }

  _showNewFileOverlay() {
    if (!this.hasNewFileOverlayTarget) return;
    this.newFileOverlayTarget.classList.remove("d-none");
    this.newFileOverlayTarget.classList.add("d-flex");
  }

  _hideNewFileOverlay() {
    if (!this.hasNewFileOverlayTarget) return;
    this.newFileOverlayTarget.classList.add("d-none");
    this.newFileOverlayTarget.classList.remove("d-flex");
  }

  _cancelNewFile() {
    if (this._newFileResolve) {
      this._newFileResolve(null);
      this._newFileResolve = null;
    }
    this._hideNewFileOverlay();
  }

  // Loads the OnlyOffice JS API script once per page lifetime.
  _ensureApiLoaded() {
    if (window.DocsAPI) return Promise.resolve();

    return new Promise((resolve, reject) => {
      if (!this.serverUrlValue) {
        reject(new Error("Document Server URL not configured (DOCUMENT_SERVER_URL missing)"));
        return;
      }
      const script    = document.createElement("script");
      script.src      = `${this.serverUrlValue}/web-apps/apps/api/documents/api.js`;
      script.async    = true;
      script.onload   = () => (window.DocsAPI ? resolve() : reject(new Error("DocsAPI not found after script load")));
      script.onerror  = () => reject(new Error(`Failed to load OnlyOffice API from ${this.serverUrlValue}`));
      document.head.appendChild(script);
    });
  }

  async _fetchConfig(configUrl) {
    const response = await fetch(configUrl, {
      headers: {
        Accept:         "application/json",
        "X-CSRF-Token": this._csrfToken,
      },
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Config fetch failed: ${response.status} — ${text.slice(0, 200)}`);
    }
    return response.json();
  }

  // CSRF token from the Rails meta tag — <meta> is in <head>, not a Stimulus target.
  get _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content ?? "";
  }
}
